import {defineSlotRecipe} from '@chakra-ui/react3'

const chakraCheckboxSlotRecipe = defineSlotRecipe({
    slots: ['root', 'label', 'control', 'indicator', 'group'],
    className: 'chakra-checkbox',
    base: {
        root: {
            display: 'inline-flex',
            gap: '2',
            alignItems: 'center',
            verticalAlign: 'top',
            position: 'relative'
        },
        control: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: '0',
            color: 'white',
            borderWidth: '1px',
            borderColor: 'transparent',
            borderRadius: 'l1',
            cursor: 'checkbox',
            focusVisibleRing: 'outside',
            _icon: {
                boxSize: 'full'
            },
            _invalid: {
                colorPalette: 'red',
                borderColor: 'border.error'
            },
            _disabled: {
                opacity: '0.5',
                cursor: 'disabled'
            }
        },
        label: {
            fontWeight: 'medium',
            userSelect: 'none',
            _disabled: {
                opacity: '0.5'
            }
        }
    },
    variants: {
        size: {
            xs: {
                root: {
                    gap: '1.5'
                },
                label: {
                    textStyle: 'xs'
                },
                control: {
                    boxSize: '3'
                }
            },
            sm: {
                root: {
                    gap: '2'
                },
                label: {
                    textStyle: 'sm'
                },
                control: {
                    boxSize: '4'
                }
            },
            md: {
                root: {
                    gap: '2.5'
                },
                label: {
                    textStyle: 'sm'
                },
                control: {
                    boxSize: '5',
                    p: '0.5'
                }
            },
            lg: {
                root: {
                    gap: '3'
                },
                label: {
                    textStyle: 'md'
                },
                control: {
                    boxSize: '6',
                    p: '0.5'
                }
            }
        },
        variant: {
            outline: {
                control: {
                    borderColor: 'border',
                    '&:is([data-state=checked], [data-state=indeterminate])': {
                        color: 'colorPalette.fg',
                        borderColor: 'colorPalette.solid'
                    }
                }
            },
            solid: {
                control: {
                    borderColor: 'border.emphasized',
                    '&:is([data-state=checked], [data-state=indeterminate])': {
                        bg: 'colorPalette.solid',
                        color: 'colorPalette.contrast',
                        borderColor: 'colorPalette.solid'
                    }
                }
            },
            subtle: {
                control: {
                    bg: 'colorPalette.muted',
                    borderColor: 'colorPalette.muted',
                    '&:is([data-state=checked], [data-state=indeterminate])': {
                        color: 'colorPalette.fg'
                    }
                }
            }
        }
    },
    defaultVariants: {
        variant: 'solid',
        size: 'md'
    }
})

export const checkboxSlotRecipe = defineSlotRecipe({
    ...chakraCheckboxSlotRecipe,
    className: 'leap-checkbox',
    base: {
        ...chakraCheckboxSlotRecipe.base,
        root: {
            ...chakraCheckboxSlotRecipe.base.root,
            width: 'fit-content',
            color: 'text.primary'
        },
        control: {
            ...chakraCheckboxSlotRecipe.base.control,
            borderRadius: 'xs',
            border: 's',
            borderColor: 'border.medium',
            backgroundColor: 'primary.base.white',
            boxShadow: 'none',
            color: 'border.brand',
            focusVisibleRing: 'outside',
            focusRingColor: 'border.strong',
            focusRingWidth: '1px',
            focusRingStyle: 'solid',

            _hover: {
                borderColor: 'border.strong'
            },
            _focus: {
                borderColor: 'border.strong'
            },
            _focusVisible: {
                borderColor: 'border.strong'
            },
            _checked: {
                backgroundColor: 'border.brand',
                borderColor: 'transparent',
                color: 'border.brand'
            },
            _invalid: {
                borderColor: 'border.system.error',
                _hover: {
                    borderColor: 'border.strong'
                },
                _focus: {
                    borderColor: 'border.system.error'
                },
                _focusVisible: {
                    borderColor: 'border.system.error'
                },
                _checked: {
                    backgroundColor: 'border.system.error',
                    borderColor: 'transparent',
                    color: 'border.system.error'
                }
            },
            _disabled: {
                ...chakraCheckboxSlotRecipe.base.control._disabled,
                cursor: 'not-allowed'
            }
        },
        indicator: {
            display: 'inline-flex',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center'
        },
        label: {
            ...chakraCheckboxSlotRecipe.base.label,
            color: 'text.primary'
        }
    },
    variants: {
        size: {
            ...chakraCheckboxSlotRecipe.variants.size,
            sm: {
                root: {
                    gap: 'spacing-10'
                },
                control: {
                    boxSize: 'spacing-14',
                    marginTop: 'spacing-2',
                    marginLeft: 'spacing-3',
                    _icon: {
                        width: '100%',
                        height: '100%'
                    }
                },
                label: {
                    textStyle: 'body.small'
                }
            }
        },
        variant: {
            outline: {
                control: {
                    borderColor: 'border.medium',
                    _checked: {
                        backgroundColor: 'border.brand',
                        borderColor: 'transparent',
                        color: 'border.brand'
                    }
                }
            }
        }
    },
    defaultVariants: {
        variant: 'outline',
        size: 'sm'
    }
})
